/**
 * Dialer modal (batch 6b) — Signal-style outbound call pad.
 *
 * Entry: `openDialer(prefill?)` — opens the modal with an E.164 input, a
 * 3×4 DTMF keypad, a "recent contacts" strip pulled from people + recent
 * calls, and a big green Dial button. Pressing Dial fires
 * `startOutbound()` which in turn surfaces the active-call component.
 */

import { showModal, closeModal, modalFrame } from './modal';
import { state } from '../state';
import { startOutbound } from './call-session';
import { get } from '../api';
import type { CallRow } from '../realtime';

const KEYS: [string, string][] = [
  ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
  ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
  ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
  ['*', ''], ['0', '+'], ['#', ''],
];

export function openDialer(prefill?: string): void {
  const body = `
    <div class="dialer" style="display:flex;flex-direction:column;gap:14px;align-items:center;padding:4px 2px">
      <input id="dialer-number" type="tel" autocomplete="off" spellcheck="false"
             value="${esc(prefill || '')}"
             placeholder="+61…"
             style="font-size:28px;text-align:center;width:100%;max-width:280px;padding:10px 12px;border:1px solid var(--border);border-radius:8px;background:var(--bg);color:var(--fg);font-family:var(--font-mono,monospace)">
      <div id="dialer-pad" style="display:grid;grid-template-columns:repeat(3,72px);gap:10px;justify-content:center">
        ${KEYS.map(([d, letters]) => `
          <button class="dialer-key" data-digit="${esc(d)}"
                  style="height:64px;border-radius:50%;border:1px solid var(--border);background:var(--bg-elev);color:var(--fg);font-size:22px;font-weight:600;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;user-select:none">
            <span>${esc(d)}</span>
            ${letters ? `<span style="font-size:9px;color:var(--fg-muted);letter-spacing:0.5px;line-height:1">${esc(letters)}</span>` : ''}
          </button>
        `).join('')}
      </div>
      <div id="dialer-recents" style="width:100%;max-width:320px;display:flex;flex-direction:column;gap:4px;margin-top:2px"></div>
    </div>
  `;
  const footer = `
    <button class="btn btn-secondary" id="dialer-cancel">Cancel</button>
    <button class="btn" id="dialer-backspace" title="Backspace" style="background:var(--bg-elev);color:var(--fg);border:1px solid var(--border)">⌫</button>
    <button class="btn btn-primary" id="dialer-dial" style="background:var(--accent, #16a34a);border:1px solid var(--accent, #16a34a);color:var(--fg-on-accent, #fff)">Dial</button>
  `;
  showModal(modalFrame('Dial', body, footer));

  const input = document.getElementById('dialer-number') as HTMLInputElement | null;
  const recentsEl = document.getElementById('dialer-recents');
  const sanitize = (v: string): string => v.replace(/[^0-9+*#]/g, '').replace(/(?!^)\+/g, '');

  if (input) {
    input.addEventListener('input', () => {
      input.value = sanitize(input.value);
    });
  }

  document.getElementById('dialer-cancel')?.addEventListener('click', closeModal);
  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('dialer-backspace')?.addEventListener('click', () => {
    if (!input) return;
    input.value = input.value.slice(0, -1);
    input.focus();
  });

  document.querySelectorAll<HTMLButtonElement>('#dialer-pad .dialer-key').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!input) return;
      input.value = sanitize(input.value + (btn.dataset.digit || ''));
      input.focus();
    });
  });

  // Hardware keypad support.
  const modalEl = document.getElementById('modal');
  const keyHandler = (e: KeyboardEvent): void => {
    if (!input) return;
    if (/^[0-9*#]$/.test(e.key)) {
      // Let native input type='tel' handle it, no-op.
      return;
    }
    if (e.key === '+' && input.value === '') {
      // Allow typed leading +.
      input.value = '+';
      e.preventDefault();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      dial();
    }
  };
  modalEl?.addEventListener('keydown', keyHandler);

  const dial = (): void => {
    if (!input) return;
    const v = input.value.trim();
    if (v.length < 3) return;
    const ownerName = state.ownerId
      ? (state.people.find(p => p.id === state.ownerId)?.name || undefined)
      : undefined;
    closeModal();
    void startOutbound(v, ownerName);
  };
  document.getElementById('dialer-dial')?.addEventListener('click', dial);

  void loadRecents(recentsEl, input);
}

async function loadRecents(recentsEl: HTMLElement | null, input: HTMLInputElement | null): Promise<void> {
  if (!recentsEl || !input) return;
  const sid = state.currentServerId ?? state.currentServer;
  if (!sid) return;
  let rows: CallRow[] = [];
  try {
    rows = await get<CallRow[]>(`/api/calls?server_id=${encodeURIComponent(sid)}&limit=20`);
  } catch { return; }
  // De-dupe by phone, newest first.
  const seen = new Set<string>();
  const uniq: CallRow[] = [];
  for (const r of rows) {
    const p = r.counterparty_phone;
    if (!p || seen.has(p)) continue;
    seen.add(p);
    uniq.push(r);
    if (uniq.length >= 5) break;
  }
  if (uniq.length === 0) return;
  recentsEl.innerHTML = `
    <div style="color:var(--fg-muted);font-size:10px;text-transform:uppercase;letter-spacing:0.4px;margin:6px 0 2px">Recent</div>
    ${uniq.map(r => `
      <button class="dialer-recent" data-phone="${esc(r.counterparty_phone)}"
              style="display:flex;align-items:center;gap:10px;padding:6px 8px;border:1px solid var(--border);border-radius:6px;background:var(--bg);cursor:pointer;text-align:left;color:var(--fg);font-size:13px">
        <span style="width:22px;height:22px;border-radius:50%;background:var(--bg-elev);display:inline-flex;align-items:center;justify-content:center;font-size:10px;color:var(--fg-muted)">${(esc((r.counterparty_name || '?').charAt(0).toUpperCase()))}</span>
        <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(r.counterparty_name || r.counterparty_phone)}</span>
        <span style="color:var(--fg-muted);font-family:var(--font-mono,monospace);font-size:12px">${esc(r.counterparty_phone)}</span>
      </button>
    `).join('')}
  `;
  recentsEl.querySelectorAll<HTMLButtonElement>('.dialer-recent').forEach(btn => {
    btn.addEventListener('click', () => {
      const phone = btn.dataset.phone || '';
      input.value = phone;
      input.focus();
    });
  });
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}
